import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSave, FaTimes, FaImage, FaVideo, FaSearch,
  FaExternalLinkAlt, FaLayerGroup
} from 'react-icons/fa';

const StudioMedia = () => {
  const { can } = usePermission();
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    type: 'photo',
    title: '',
    slug: '',
    category: '',
    url: '',
    caption: '',
    media: [],
    existing_media: [],
    order_index: 0,
    active: true
  });

  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [managingSections, setManagingSections] = useState(null);
  const [studioSections, setStudioSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);

  const [sectionFormData, setSectionFormData] = useState({
    section_title: '',
    section_description: '',
    section_url: '',
    order_index: 0,
    gallery: [],
    existingGallery: [],
    highlights: [],
    section_tags: []
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  useEffect(() => {
    fetchMediaItems();
  }, [currentPage, searchTerm, filterType, filterActive]);

  const fetchMediaItems = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 12,
        ...(filterType !== 'all' && { type: filterType })
      };

      const response = await axios.get(`${SERVER}/content/admin/studio-media`, {
        params,
        withCredentials: true
      });

      // Filter by active status on frontend since backend doesn't support it yet
      let filteredItems = response.data.media;
      if (filterActive !== 'all') {
        filteredItems = filteredItems.filter(item => 
          filterActive === 'active' ? item.active : !item.active
        );
      }

      // Filter by search term
      if (searchTerm) {
        filteredItems = filteredItems.filter(item =>
          item.caption?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setMediaItems(filteredItems);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching media items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingItem) return;
    if (!canEdit && editingItem) return;

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (key === 'media') {
          // Only append new files
          if (Array.isArray(formData[key])) {
            formData[key].forEach(file => {
              if (file instanceof File) {
                formDataToSend.append('media', file);
              }
            });
          }
        } else if (key === 'existing_media') {
          // Send existing media paths if no new media uploaded
          if (formData[key] && formData[key].length > 0 && (!formData.media || formData.media.length === 0)) {
            formDataToSend.append('existing_media', JSON.stringify(formData[key]));
          }
        } else if (key !== 'media' && key !== 'existing_media') {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingItem) {
        response = await axios.put(`${SERVER}/content/admin/studio-media/${editingItem.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/content/admin/studio-media`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchMediaItems();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving media item:', error);
    }
  };

  const handleEdit = async (item) => {
    if (!canEdit) return;
    
    try {
      const response = await axios.get(`${SERVER}/content/admin/studio-media/${item.id}`, {
        withCredentials: true
      });
      
      const itemData = response.data;
      console.log('Fetched studio media data for editing:', itemData); // Debug log
      
      // Handle existing media - could be gallery array or single url
      let existingMedia = [];
      if (itemData.gallery && Array.isArray(itemData.gallery)) {
        existingMedia = itemData.gallery;
      } else if (itemData.url && itemData.type === 'photo') {
        // For photos, if there's a URL but no gallery, use the URL
        existingMedia = [itemData.url];
      }
      
      console.log('Existing media extracted:', existingMedia); // Debug log
      
      setFormData({
        type: itemData.type || 'photo',
        title: itemData.title || '',
        slug: itemData.slug || '',
        category: itemData.category || '',
        url: itemData.url || '',
        caption: itemData.caption || '',
        media: [],
        existing_media: existingMedia,
        order_index: itemData.order_index || 0,
        active: itemData.active
      });
      setEditingItem(item);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching media item details:', error);
      alert('Error loading media item details. Please try again.');
    }
  };

  const handleDelete = async (item) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete this ${item.type}?`)) {
      try {
        await axios.delete(`${SERVER}/content/admin/studio-media/${item.id}`, {
          withCredentials: true
        });
        fetchMediaItems();
      } catch (error) {
        console.error('Error deleting media item:', error);
      }
    }
  };

  const handleToggleActive = async (item) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/content/admin/studio-media/${item.id}`, {
        ...item,
        active: !item.active
      }, {
        withCredentials: true
      });
      fetchMediaItems();
    } catch (error) {
      console.error('Error toggling media status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      type: 'photo',
      title: '',
      slug: '',
      category: '',
      url: '',
      caption: '',
      media: null,
      existing_media: [],
      order_index: 0,
      active: true
    });
    setEditingItem(null);
    setShowModal(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Section management functions
  const fetchStudioSections = async (studioId) => {
    try {
      setSectionsLoading(true);
      const response = await axios.get(`${SERVER}/content/admin/studio-media/${studioId}/sections`, {
        withCredentials: true
      });
      setStudioSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching studio sections:', error);
      setStudioSections([]);
    } finally {
      setSectionsLoading(false);
    }
  };

  const openSectionManager = async (item) => {
    if (!canEdit) return;
    setManagingSections(item);
    await fetchStudioSections(item.id);
  };

  const closeSectionManager = () => {
    setManagingSections(null);
    setStudioSections([]);
    setShowSectionModal(false);
    setEditingSection(null);
  };

  const openSectionModal = (section = null) => {
    if (section) {
      // Ensure gallery is properly formatted for display
      // Backend returns array of objects with image_url property
      const existingGallery = (section.gallery || []).map(img => {
        // If it's already an object with image_url, keep it
        if (typeof img === 'object' && img.image_url) {
          return img.image_url;
        }
        // If it's a string, use it directly
        return img;
      });
      
      setSectionFormData({
        section_title: section.section_title || '',
        section_description: section.section_description || '',
        section_url: section.section_url || '',
        order_index: section.order_index || 0,
        gallery: [], // New files to upload
        existingGallery: existingGallery, // Existing image URLs as strings
        highlights: (section.properties || []).map(property => ({
          title: property.property_title || ''
        })), // Map properties to highlights with only title
        section_tags: Array.isArray(section.section_tags) ? section.section_tags : []
      });
      setEditingSection(section);
    } else {
      setSectionFormData({
        section_title: '',
        section_description: '',
        section_url: '',
        order_index: studioSections.length,
        gallery: [],
        existingGallery: [],
        highlights: [],
        section_tags: []
      });
      setEditingSection(null);
    }
    setShowSectionModal(true);
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    if (!managingSections) return;

    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('section_title', sectionFormData.section_title);
      formDataToSend.append('section_description', sectionFormData.section_description);
      formDataToSend.append('section_url', sectionFormData.section_url);
      formDataToSend.append('order_index', sectionFormData.order_index);
      formDataToSend.append('section_tags', JSON.stringify(sectionFormData.section_tags));
      
      const properties = sectionFormData.highlights.map(highlight => ({
        property_title: highlight.title,
        property_description: '',
        icon: '✓'
      }));
      formDataToSend.append('properties', JSON.stringify(properties));
      
      formDataToSend.append('existingGallery', JSON.stringify(sectionFormData.existingGallery));
      
      if (sectionFormData.gallery && sectionFormData.gallery.length > 0) {
        sectionFormData.gallery.forEach(file => {
          if (file instanceof File) {
            formDataToSend.append('gallery', file);
          }
        });
      }

      let response;
      if (editingSection) {
        response = await axios.put(
          `${SERVER}/content/admin/studio-media/${managingSections.id}/sections/${editingSection.id}`, 
          formDataToSend, 
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      } else {
        response = await axios.post(
          `${SERVER}/content/admin/studio-media/${managingSections.id}/sections`, 
          formDataToSend, 
          {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
      }

      if (response.status === 200 || response.status === 201) {
        await fetchStudioSections(managingSections.id);
        setShowSectionModal(false);
        setEditingSection(null);
        setSectionFormData({
          section_title: '',
          section_description: '',
          section_url: '',
          order_index: 0,
          gallery: [],
          existingGallery: [],
          highlights: [],
          section_tags: []
        });
      }
    } catch (error) {
      console.error('Error saving section:', error);
      const errorMessage = error.response?.data?.error || 'Error saving section. Please try again.';
      alert(errorMessage);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!managingSections || !canDelete) return;
    
    if (window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
      try {
        await axios.delete(`${SERVER}/content/admin/studio-media/${managingSections.id}/sections/${sectionId}`, {
          withCredentials: true
        });
        await fetchStudioSections(managingSections.id);
      } catch (error) {
        console.error('Error deleting section:', error);
        const errorMessage = error.response?.data?.error || 'Error deleting section. Please try again.';
        alert(errorMessage);
      }
    }
  };

  // Section highlight helpers
  const addSectionHighlight = () => {
    setSectionFormData({
      ...sectionFormData,
      highlights: [...sectionFormData.highlights, { title: '' }]
    });
  };

  const updateSectionHighlight = (index, field, value) => {
    const updatedHighlights = [...sectionFormData.highlights];
    updatedHighlights[index][field] = value;
    setSectionFormData({
      ...sectionFormData,
      highlights: updatedHighlights
    });
  };

  const removeSectionHighlight = (index) => {
    const updatedHighlights = sectionFormData.highlights.filter((_, i) => i !== index);
    setSectionFormData({
      ...sectionFormData,
      highlights: updatedHighlights
    });
  };

  // Section tags helpers
  const addSectionTag = () => {
    setSectionFormData({
      ...sectionFormData,
      section_tags: [...sectionFormData.section_tags, '']
    });
  };

  const updateSectionTag = (index, value) => {
    const updatedTags = [...sectionFormData.section_tags];
    updatedTags[index] = value;
    setSectionFormData({
      ...sectionFormData,
      section_tags: updatedTags
    });
  };

  const removeSectionTag = (index) => {
    const updatedTags = sectionFormData.section_tags.filter((_, i) => i !== index);
    setSectionFormData({
      ...sectionFormData,
      section_tags: updatedTags
    });
  };

  // Section gallery helpers
  const handleSectionGalleryChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setSectionFormData({
      ...sectionFormData,
      gallery: newFiles // Replace instead of append to avoid duplication
    });
  };

  const removeExistingImage = (imageIndex) => {
    const updatedExistingGallery = sectionFormData.existingGallery.filter((_, i) => i !== imageIndex);
    setSectionFormData({
      ...sectionFormData,
      existingGallery: updatedExistingGallery
    });
  };

  const removeNewImage = (fileIndex) => {
    const updatedGallery = sectionFormData.gallery.filter((_, i) => i !== fileIndex);
    setSectionFormData({
      ...sectionFormData,
      gallery: updatedGallery
    });
  };

  const isVideoUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com'));
  };

  const getVideoThumbnail = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop().split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Studio Media Management</h2>
          <p className="text-slate-600">Manage your creative studio photos and videos</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Media</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search media..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="photo">Photos Only</option>
              <option value="video">Videos Only</option>
            </select>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Media</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Media Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {mediaItems.map((item) => (
          <div key={item.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="aspect-square bg-slate-100 relative">
              {item.type === 'photo' ? (
                <div className="relative">
                  <img
                    src={`${SERVER}${item.url}`}
                    alt={item.caption}
                    className="w-full h-full object-cover"
                  />
                  {/* Gallery indicator */}
                  {item.gallery && Array.isArray(item.gallery) && item.gallery.length > 1 && (
                    <div className="absolute bottom-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                      +{item.gallery.length - 1} more
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-200 relative">
                  {isVideoUrl(item.url) && getVideoThumbnail(item.url) ? (
                    <img
                      src={getVideoThumbnail(item.url)}
                      alt={item.caption}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <FaVideo className="text-4xl text-slate-400" />
                  )}
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                      <FaVideo className="text-2xl text-slate-600" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Type Badge */}
              <div className="absolute top-3 left-3">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.type === 'photo' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {item.type === 'photo' ? <FaImage className="inline mr-1" /> : <FaVideo className="inline mr-1" />}
                  {item.type}
                </div>
              </div>

              {/* Status Badge */}
              <div className="absolute top-3 right-3">
                <button
                  onClick={() => handleToggleActive(item)}
                  disabled={!canEdit}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                    item.active
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-red-100 text-red-800 hover:bg-red-200'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  {item.active ? <FaToggleOn className="inline mr-1" /> : <FaToggleOff className="inline mr-1" />}
                  {item.active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
            
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-800 mb-1">
                    {item.title || 'Untitled'}
                  </h4>
                  <p className="text-sm text-slate-600 mb-2">
                    {item.category || 'No category'}
                  </p>
                  <p className="text-xs text-slate-500 line-clamp-2 mb-2">
                    {item.caption || 'No caption'}
                  </p>
                  <div className="text-xs text-slate-500">
                    Order: {item.order_index}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {item.type === 'video' && (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Video"
                    >
                      <FaExternalLinkAlt />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openSectionManager(item)}
                    disabled={!canEdit}
                    className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Manage Sections"
                  >
                    <FaLayerGroup />
                  </button>
                  <button
                    onClick={() => handleEdit(item)}
                    disabled={!canEdit}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(item)}
                    disabled={!canDelete}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {mediaItems.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-3xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">No Media Found</h3>
          <p className="text-slate-600 mb-4">
            {searchTerm || filterType !== 'all' || filterActive !== 'all'
              ? 'No media items match your current filters.'
              : 'Start by adding some photos or videos to your studio gallery.'}
          </p>
          {canCreate && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            >
              <FaPlus />
              Add Media
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingItem ? 'Edit Media' : 'Add New Media'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Media Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="photo">Photo</option>
                  <option value="video">Video</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Asymmetry, Malakite"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    URL Slug *
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="studio-project-slug"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Clothing photography, Product photography"
                  />
                </div>
              </div>

              {formData.type === 'photo' ? (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Photo Files * (Multiple files supported)
                  </label>
                  
                  {editingItem && formData.existing_media && formData.existing_media.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">Current Photos ({formData.existing_media.length}):</p>
                      <div className="grid grid-cols-4 gap-2">
                        {formData.existing_media.slice(0, 8).map((img, idx) => (
                          <img
                            key={idx}
                            src={`${SERVER}${img}`}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg border border-slate-200"
                            onError={(e) => {
                              console.error('Failed to load studio image:', `${SERVER}${img}`);
                              e.target.style.opacity = '0.3';
                            }}
                          />
                        ))}
                        {formData.existing_media.length > 8 && (
                          <div className="w-full h-20 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center text-sm text-slate-600">
                            +{formData.existing_media.length - 8} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {formData.media && formData.media.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm text-slate-600 mb-2">New Photos to Upload ({formData.media.length}):</p>
                      <div className="grid grid-cols-4 gap-2">
                        {formData.media.slice(0, 8).map((file, idx) => (
                          <img
                            key={idx}
                            src={URL.createObjectURL(file)}
                            alt={`New photo ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg border-2 border-blue-300"
                          />
                        ))}
                        {formData.media.length > 8 && (
                          <div className="w-full h-20 bg-blue-50 rounded-lg border-2 border-blue-300 flex items-center justify-center text-sm text-blue-600">
                            +{formData.media.length - 8} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => setFormData({ ...formData, media: Array.from(e.target.files) })}
                    required={!editingItem}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    {editingItem 
                      ? 'Leave empty to keep current photos. Select multiple files to replace all photos.'
                      : 'Select one or more photos for this studio item. First photo will be the main image.'}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Video URL *
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    Supports YouTube, Vimeo, and direct video URLs
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Caption
                </label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description of the media"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    value={formData.order_index}
                    onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-slate-700">Active</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingItem ? 'Update Media' : 'Add Media'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Section Management Modal */}
      {managingSections && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  Manage Sections - {managingSections.title}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openSectionModal()}
                    disabled={!canCreate}
                    className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaPlus />
                    Add Section
                  </button>
                  <button
                    onClick={closeSectionManager}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {sectionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                </div>
              ) : studioSections.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaLayerGroup className="text-3xl text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Sections Found</h3>
                  <p className="text-slate-600 mb-4">
                    Start by adding sections to make this studio item dynamic.
                  </p>
                  {canCreate && (
                    <button
                      onClick={() => openSectionModal()}
                      className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300"
                    >
                      <FaPlus />
                      Add First Section
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {studioSections.map((section) => (
                    <div key={section.id} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-slate-800 mb-2">
                            {section.section_title}
                          </h4>
                          <p className="text-slate-600 text-sm mb-2">
                            {section.section_description}
                          </p>
                          {section.section_url && (
                            <a
                              href={section.section_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                            >
                              <FaExternalLinkAlt className="text-xs" />
                              View Section
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500 bg-slate-200 px-2 py-1 rounded">
                            Order: {section.order_index}
                          </span>
                          <button
                            onClick={() => openSectionModal(section)}
                            disabled={!canEdit}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit Section"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleDeleteSection(section.id)}
                            disabled={!canDelete}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete Section"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Gallery ({section.gallery?.length || 0} images)</h5>
                          {section.gallery && section.gallery.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto">
                              {section.gallery.slice(0, 3).map((image, imgIndex) => (
                                <img
                                  key={imgIndex}
                                  src={`${SERVER}${image.image_url}`}
                                  alt={image.image_title || `Section image ${imgIndex + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                />
                              ))}
                              {section.gallery.length > 3 && (
                                <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center text-xs text-slate-600">
                                  +{section.gallery.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Highlights ({section.properties?.length || 0})</h5>
                          {section.properties && section.properties.length > 0 && (
                            <div className="space-y-1">
                              {section.properties.slice(0, 3).map((property, propertyIndex) => (
                                <div key={propertyIndex} className="text-xs text-slate-600 flex items-center gap-2">
                                  <span className="text-blue-600">{property.icon || '✓'}</span>
                                  {property.property_title}
                                </div>
                              ))}
                              {section.properties.length > 3 && (
                                <div className="text-xs text-slate-500">
                                  +{section.properties.length - 3} more highlights
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div>
                          <h5 className="text-sm font-semibold text-slate-700 mb-2">Tags ({section.section_tags?.length || 0})</h5>
                          {section.section_tags && section.section_tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {section.section_tags.slice(0, 3).map((tag, tagIndex) => (
                                <span
                                  key={tagIndex}
                                  className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs"
                                >
                                  {tag}
                                </span>
                              ))}
                              {section.section_tags.length > 3 && (
                                <span className="text-xs text-slate-500">
                                  +{section.section_tags.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Section Form Modal */}
      {showSectionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingSection ? 'Edit Section' : 'Add New Section'}
                </h3>
                <button
                  onClick={() => setShowSectionModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSectionSubmit} className="p-6 space-y-6">
              {/* Section Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Section Title *
                  </label>
                  <input
                    type="text"
                    value={sectionFormData.section_title}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, section_title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="e.g., Product Photography, Fashion Shoot"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    value={sectionFormData.order_index}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section Description
                </label>
                <textarea
                  value={sectionFormData.section_description}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, section_description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Describe what this section covers..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section URL (Optional)
                </label>
                <input
                  type="url"
                  value={sectionFormData.section_url}
                  onChange={(e) => setSectionFormData({ ...sectionFormData, section_url: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="https://example.com"
                />
              </div>

              {/* Section Gallery */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Section Gallery Images
                </label>
                
                {/* Existing Images */}
                {sectionFormData.existingGallery.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-600 mb-2">Current Images ({sectionFormData.existingGallery.length})</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sectionFormData.existingGallery.map((image, index) => {
                        const imagePath = typeof image === 'string' ? image : (image.image_url || image);
                        const imageTitle = typeof image === 'object' ? image.image_title : null;
                        
                        return (
                          <div key={index} className="relative group">
                            <img
                              src={`${SERVER}${imagePath}`}
                              alt={imageTitle || `Gallery image ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-slate-200"
                            />
                            <button
                              type="button"
                              onClick={() => removeExistingImage(index)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                              title="Remove image"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* New Images Preview */}
                {sectionFormData.gallery.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-600 mb-2">New Images to Upload</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {sectionFormData.gallery.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New upload ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-slate-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewImage(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-all duration-200 shadow-lg"
                            title="Remove image"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* File Upload */}
                <input
                  key={editingSection ? `edit-${editingSection.id}` : 'new'}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSectionGalleryChange}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Select multiple images to add to the gallery
                </p>
              </div>

              {/* Section Highlights */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Section Highlights
                  </label>
                  <button
                    type="button"
                    onClick={addSectionHighlight}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Highlight
                  </button>
                </div>
                
                <div className="space-y-4">
                  {sectionFormData.highlights.map((highlight, index) => (
                    <div key={index} className="flex gap-4 items-center p-4 bg-slate-50 rounded-xl">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={highlight.title}
                          onChange={(e) => updateSectionHighlight(index, 'title', e.target.value)}
                          placeholder="Highlight title"
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeSectionHighlight(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section Tags */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-semibold text-slate-700">
                    Section Tags ({sectionFormData.section_tags.length})
                  </label>
                  <button
                    type="button"
                    onClick={addSectionTag}
                    className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                  >
                    <FaPlus />
                    Add Tag
                  </button>
                </div>
                
                {sectionFormData.section_tags.length === 0 ? (
                  <div className="text-sm text-slate-500 italic p-4 bg-slate-50 rounded-lg">
                    No tags added yet. Click "Add Tag" to add section tags.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sectionFormData.section_tags.map((tag, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={tag}
                          onChange={(e) => updateSectionTag(index, e.target.value)}
                          placeholder="Tag name"
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeSectionTag(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowSectionModal(false)}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingSection ? 'Update Section' : 'Create Section'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudioMedia;